import { fetchAccumulatedOfferById } from 'actions/OfferActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getAllOffers } from 'selectors/offerSelectors'
import { WEDDING_VENUES_LIST, type VenueInspirationOption, type WeddingVenueListItem } from 'weddings/constants/venuesList'

export interface WeddingVenueInspiration extends Omit<WeddingVenueListItem, 'messages'> {
  location: string
  title: string
  guestCapacity: string
  season: string
  seasonHint?: string
  description: string
  highlights: Array<string>
  offer?: App.HotelOffer | App.BedbankOffer | App.VillaOffer
  images: Array<App.Image>
  rating?: App.OfferRating
  amenities: Array<string>
}

interface UseVenuesListResult {
  venues: Array<WeddingVenueInspiration>
  isLoading: boolean
}

function isAccommodationOffer(offer: App.AnyOffer | undefined): offer is App.HotelOffer | App.BedbankOffer | App.VillaOffer {
  return !!offer && 'property' in offer
}

function useVenuesList(selectedOption: VenueInspirationOption): UseVenuesListResult {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const offersById = useAppSelector(getAllOffers)
  const offersLoading = useAppSelector(state => state.offer.offersLoading)
  const offerErrors = useAppSelector(state => state.offer.offerErrors)

  const selectedVenues = useMemo(() => WEDDING_VENUES_LIST.filter((venue) => (
    venue.optionType === selectedOption
  )), [selectedOption])

  useEffect(() => {
    selectedVenues.forEach(({ offerId }) => {
      dispatch(fetchAccumulatedOfferById(offerId, undefined, undefined, true))
    })
  }, [dispatch, selectedVenues])

  return useMemo(() => {
    const venues = selectedVenues.map((venue) => {
      const offer = offersById[venue.offerId]
      const accommodationOffer = isAccommodationOffer(offer) ? offer : undefined

      return {
        ...venue,
        location: intl.formatMessage(venue.messages.location),
        title: intl.formatMessage(venue.messages.title),
        guestCapacity: intl.formatMessage(venue.messages.guestCapacity),
        season: intl.formatMessage(venue.messages.season),
        seasonHint: venue.messages.seasonHint ? intl.formatMessage(venue.messages.seasonHint) : undefined,
        description: intl.formatMessage(venue.messages.description),
        highlights: venue.messages.highlights.map((highlight) => intl.formatMessage(highlight)),
        offer: accommodationOffer,
        images: accommodationOffer?.images ?? [],
        rating: accommodationOffer?.property.rating,
        amenities: [],
      }
    })

    return {
      venues,
      isLoading: selectedVenues.some(({ offerId }) => (
        (!!offersLoading[offerId] || !offersById[offerId]) && !offerErrors[offerId]
      )),
    }
  }, [intl, offerErrors, offersById, offersLoading, selectedVenues])
}

export default useVenuesList
